import { useEffect, useRef, useState } from 'react';
import { visibleReleases } from './filter';
import { compareSemver, newestVersion } from './semver';
import type { ReleaseNotesAdapter, ReleaseNotesFile, ReleaseNotesRelease } from './types';

const OPEN_DELAY_MS = 600;

function viewerOf(adapter: ReleaseNotesAdapter) {
  return { features: adapter.features };
}

/**
 * Loads /release-notes.json once the signed-in shell is up.
 * The first visit records the current version and stays quiet.
 * Later visits open one window for every release since that version.
 */
export function useReleaseNotes(adapter: ReleaseNotesAdapter) {
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;
  const decided = useRef(false);

  const [file, setFile] = useState<ReleaseNotesFile | null>(null);
  const [fileState, setFileState] = useState<'idle' | 'loading' | 'ready' | 'missing'>('idle');
  const [lastSeen, setLastSeen] = useState<string | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'unseen' | 'history'>('unseen');
  const [releases, setReleases] = useState<ReleaseNotesRelease[]>([]);

  useEffect(() => {
    if (!adapter.ready) return undefined;
    let cancelled = false;
    setFileState('loading');
    fetch('/release-notes.json', { cache: 'no-store' })
      .then(async (res) => {
        if (!res.ok) return null;
        try {
          return JSON.parse(await res.text()) as ReleaseNotesFile;
        } catch {
          return null;
        }
      })
      .then((json) => {
        if (cancelled) return;
        if (!json || !Array.isArray(json.releases)) {
          setFile(null);
          setFileState('missing');
          return;
        }
        setFile(json);
        setFileState('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setFile(null);
          setFileState('missing');
        }
      });
    return () => { cancelled = true; };
  }, [adapter.ready]);

  useEffect(() => {
    if (!adapter.ready) return undefined;
    let cancelled = false;
    adapterRef.current.loadLastSeen()
      .then((version) => { if (!cancelled) setLastSeen(version); })
      .catch(() => { /* leave unknown so a failed read is not treated as never seen */ });
    return () => { cancelled = true; };
  }, [adapter.ready]);

  useEffect(() => {
    if (!adapter.ready || fileState !== 'ready' || !file || lastSeen === undefined) return undefined;
    if (decided.current) return undefined;
    const newest = newestVersion(file);
    if (!newest) {
      decided.current = true;
      return undefined;
    }
    if (lastSeen == null) {
      decided.current = true;
      adapterRef.current.saveLastSeen(newest).catch(() => { decided.current = false; });
      return undefined;
    }
    if (adapter.suppressAutoOpen) {
      decided.current = true;
      return undefined;
    }
    const pending = visibleReleases(file.releases, viewerOf(adapterRef.current))
      .filter((release) => compareSemver(release.version, lastSeen) > 0);
    if (pending.length === 0) {
      decided.current = true;
      return undefined;
    }
    const timer = window.setTimeout(() => {
      decided.current = true;
      const stillPending = visibleReleases(file.releases, viewerOf(adapterRef.current))
        .filter((release) => compareSemver(release.version, lastSeen) > 0);
      if (stillPending.length === 0) return;
      setMode('unseen');
      setReleases(stillPending);
      setOpen(true);
    }, OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [adapter.ready, adapter.suppressAutoOpen, adapter.features, file, fileState, lastSeen]);

  const dismiss = () => {
    setOpen(false);
    const newest = file ? newestVersion(file) : null;
    if (!newest) return;
    setLastSeen(newest);
    adapterRef.current.saveLastSeen(newest).catch(() => { /* next visit tries again */ });
  };

  const openHistory = () => {
    setMode('history');
    setReleases(file ? visibleReleases(file.releases, viewerOf(adapterRef.current)) : []);
    setOpen(true);
  };

  return { open, mode, releases, dismiss, openHistory, appName: adapter.appName };
}
